import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  type AfterViewInit,
  type OnDestroy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Currency, Money } from '@principle-theorem/accounting';
import {
  AppointmentDepositCaptureComponent,
  type AppointmentDepositDialogResult,
  type IAppointmentDepositCaptureData,
} from '@principle-theorem/ng-payments';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import {
  DepositRequestDeliveryChannel,
  type IAppointment,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import { type IAppointmentDepositCapturePresenter } from '../../appointment-deposit-capture-presenter';
import { TakeDepositEntry } from '../../take-deposit-entry';
import { RecordAppointmentDepositService } from '../../record-appointment-deposit.service';

export enum TakeDepositStep {
  Method = 'method',
  Channels = 'channels',
  Capture = 'capture',
}

/** Lives with the panel so both hosts confirm with the same words. */
export const depositRecordedMessage = 'Deposit recorded';

/**
 * Takes the deposit for an appointment. The operator chooses to take payment
 * in person, send a payment link or skip, and the panel tells its host whether
 * the deposit was captured. Used inline in the appointment view sidebar and
 * inside the Take Deposit dialog.
 *
 * Every step before the deposit is recorded leads back to the method chooser,
 * where skipping is offered, because the dialog host has no other way out.
 */
@Component({
  selector: 'pr-take-deposit-panel',
  templateUrl: './take-deposit-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    AppointmentDepositCaptureComponent,
  ],
})
export class TakeDepositPanelComponent
  implements AfterViewInit, OnDestroy, IAppointmentDepositCapturePresenter
{
  private readonly _recordDeposit = inject(RecordAppointmentDepositService);
  /**
   * Whether this panel is still around to report an outcome.
   *
   * The record chain is awaited across an operator-paced capture, so the host
   * can tear this panel down before it resolves. The deposit is already
   * persisted by then — the only thing lost is who to tell — so a destroyed
   * panel returns quietly instead of throwing NG0953 out of a money path.
   */
  private _destroyed = false;
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private _resolveCapture:
    | ((result: AppointmentDepositDialogResult | undefined) => void)
    | undefined;
  private _backedOutOfCapture = false;

  readonly appointment = input.required<WithRef<IAppointment>>();
  /** Defaults to the use where leaving costs nothing. */
  readonly entry = input<TakeDepositEntry>(TakeDepositEntry.Revisit);
  readonly sendPaymentLink = output<DepositRequestDeliveryChannel>();
  /** The deposit is persisted as paid. Hosts may confirm it unconditionally. */
  readonly captured = output<void>();
  /**
   * The panel is finished without a deposit. Emitted when the operator skips,
   * and also when a capture failed — the record chain reports its own failures,
   * so a host that confirmed here would report the same attempt twice.
   */
  readonly skipped = output<void>();

  readonly step = signal(TakeDepositStep.Method);
  readonly captureData = signal<IAppointmentDepositCaptureData | undefined>(
    undefined
  );
  readonly recording = signal(false);
  readonly busy = computed(() => this.recording());
  readonly mustDecide = computed(
    () => this.entry() === TakeDepositEntry.Booking
  );
  readonly formattedAmount = computed(() => {
    const deposit = this.appointment().deposit;
    if (!deposit) {
      return '';
    }
    return Money.formatPlainCents(
      deposit.metadata.amountCents,
      deposit.metadata.currency ?? Currency.AUD
    );
  });

  readonly TakeDepositStep = TakeDepositStep;
  readonly DepositRequestDeliveryChannel = DepositRequestDeliveryChannel;

  ngAfterViewInit(): void {
    this._elementRef.nativeElement.scrollIntoView({ block: 'center' });
  }

  ngOnDestroy(): void {
    this._destroyed = true;
  }

  /**
   * Hands the amount and payment-method step to the operator. The returned
   * promise settles when they either capture or back out, which is when the
   * record chain resumes.
   */
  async present(
    data: IAppointmentDepositCaptureData
  ): Promise<AppointmentDepositDialogResult | undefined> {
    this.captureData.set(data);
    this.step.set(TakeDepositStep.Capture);
    return new Promise<AppointmentDepositDialogResult | undefined>(
      (resolve) => (this._resolveCapture = resolve)
    );
  }

  async recordDeposit(): Promise<void> {
    if (this.busy()) {
      return;
    }
    this._backedOutOfCapture = false;
    this.recording.set(true);
    let recorded = false;
    try {
      recorded = await this._recordDeposit.record(this.appointment(), this);
    } finally {
      this.recording.set(false);
      this.captureData.set(undefined);
      this._resolveCapture = undefined;
    }
    if (this._backedOutOfCapture) {
      this.step.set(TakeDepositStep.Method);
      return;
    }
    if (this._destroyed) {
      return;
    }
    if (recorded) {
      this.captured.emit();
      return;
    }
    this.skipped.emit();
  }

  captureCompleted(result: AppointmentDepositDialogResult): void {
    this._settleCapture(result);
  }

  /**
   * Leaves the capture step without capturing. The record chain treats an
   * unresolved capture as "no capture" and writes nothing, so this is safe for
   * as long as it is offered.
   */
  leaveCapture(): void {
    this._backedOutOfCapture = true;
    this.step.set(TakeDepositStep.Method);
    this._settleCapture(undefined);
  }

  showChannels(): void {
    if (this.busy()) {
      return;
    }
    this.step.set(TakeDepositStep.Channels);
  }

  showMethods(): void {
    if (this.busy()) {
      return;
    }
    this.step.set(TakeDepositStep.Method);
  }

  sendLink(deliveryChannel: DepositRequestDeliveryChannel): void {
    if (this.busy()) {
      return;
    }
    this.sendPaymentLink.emit(deliveryChannel);
  }

  skip(): void {
    if (this.busy()) {
      return;
    }
    this.skipped.emit();
  }

  private _settleCapture(result?: AppointmentDepositDialogResult): void {
    const resolve = this._resolveCapture;
    this._resolveCapture = undefined;
    resolve?.(result);
  }
}
